import { AppFooter } from "@/components/app-footer";
import { AppHeader } from "@/components/app-header";
import { LocaleLink } from "@/components/locale-link";
import { useTranslation } from "@/common/providers/i18n-provider";
import { selectFeaturedComparePages } from "@/config/compare-featured";
import type { ComparePage } from "@/lib/pages";
import "@/styles/company-pages.css";
import "@/styles/compare.css";

/** One titled grid of comparison cards; renders nothing when empty. */
function CompareGroup({
  heading,
  description,
  id,
  pages,
  cardCta,
  featured = false,
}: {
  heading: string;
  description?: string;
  id: string;
  pages: ComparePage[];
  cardCta: string;
  /** The lead block: title + action cards; the full card follows in its group. */
  featured?: boolean;
}) {
  if (pages.length === 0) return null;

  return (
    <section id={id} className="compare-hub-group">
      <h2>{heading}</h2>
      {description ? <p>{description}</p> : null}
      <div className="compare-link-grid">
        {pages.map((page) => (
          <LocaleLink
            key={page.slug}
            to="/compare/$slug"
            params={{ slug: page.slug }}
            className="compare-link"
          >
            <h3>{page.title}</h3>
            {featured ? null : <p>{page.description}</p>}
            <span className="compare-link-action">{cardCta}</span>
          </LocaleLink>
        ))}
      </div>
    </section>
  );
}

export function CompareHub({ pages }: { pages: ComparePage[] }) {
  const { t } = useTranslation();
  const roundups = pages.filter((page) => page.kind === "roundup");
  const versus = pages.filter((page) => page.kind !== "roundup");
  // Featured cards repeat in their own group below on purpose: the
  // alphabetical groups stay complete, the featured block only adds a lead.
  const featuredPages = selectFeaturedComparePages(pages);
  const cardCta = t("compare.hub.cardCta");

  return (
    <div className="company-page compare-page">
      <AppHeader />
      <main id="main">
        <section className="compare-hub-hero">
          <div className="company-container">
            <p className="compare-context">{t("compare.hub.eyebrow")}</p>
            <h1>{t("compare.hub.title")}</h1>
            <p className="company-lead">{t("compare.hub.description")}</p>
            {roundups.length > 0 && versus.length > 0 ? (
              <nav
                className="compare-jumps"
                aria-label={t("compare.hub.title")}
              >
                <a href="#roundups">{t("compare.hub.roundupsTitle")}</a>
                <a href="#head-to-head">{t("compare.hub.versusTitle")}</a>
              </nav>
            ) : null}
          </div>
        </section>

        <div className="company-container compare-hub-groups">
          <CompareGroup
            heading={t("compare.hub.featuredTitle")}
            description={t("compare.hub.featuredDescription")}
            id="featured"
            pages={featuredPages}
            cardCta={cardCta}
            featured
          />
          {/* Roundups first: a "best X alternatives" page is the broader entry
              point, and each one hands off to the head-to-head pages below. */}
          <CompareGroup
            heading={t("compare.hub.roundupsTitle")}
            id="roundups"
            pages={roundups}
            cardCta={cardCta}
          />
          <CompareGroup
            heading={t("compare.hub.versusTitle")}
            id="head-to-head"
            pages={versus}
            cardCta={cardCta}
          />
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
